import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function CtaSection() {
  const ref = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(el); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const features = [
    { icon: 'ri-time-line', title: '빠른 응답', desc: '24시간 내 전문 상담사 연락' },
    { icon: 'ri-money-dollar-circle-line', title: '무료 상담', desc: '부담 없는 무료 1:1 상담' },
    { icon: 'ri-map-pin-line', title: '방문 상담', desc: '서울 중구 지부 방문 가능' },
    { icon: 'ri-award-line', title: '자격증 안내', desc: '취득 절차 상세 안내 제공' },
  ];

  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="py-16 md:py-20 bg-[#1B3A6B]">
      <div className="max-w-7xl mx-auto px-5 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-12 items-center">
          <div className={`text-center lg:text-left anim-slide-left ${isVisible ? 'is-visible' : ''}`}>
            <span className="inline-block text-xs font-bold tracking-widest text-[#C9A84C] uppercase border border-[#C9A84C]/40 rounded-sm px-3 py-1 mb-6">상담 신청</span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-white mb-4 leading-tight">
              지금 바로 무료 상담을<br />신청하세요
            </h2>
            <p className="text-white/60 text-sm leading-relaxed mb-8">
              전문 상담사가 24시간 내 연락드립니다.<br />
              수강료, 교육 일정, 자격증 취득까지 모든 궁금증을 해결해 드립니다.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/academy#consult"
                className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md bg-[#C9A84C] text-white font-bold hover:bg-[#b8963e] transition-colors cursor-pointer w-full sm:w-auto"
              >
                <i className="ri-edit-line" /> 온라인 상담 신청
              </Link>
              <a
                href="tel:010-6740-4061"
                className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md border-2 border-white/30 text-white font-semibold hover:bg-white/10 transition-colors cursor-pointer w-full sm:w-auto"
              >
                <i className="ri-phone-line" /> 010-6740-4061
              </a>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {features.map((item, i) => (
              <div
                key={item.title}
                className={`bg-white/10 rounded-xl p-4 md:p-5 border border-white/10 anim-scale-up ${isVisible ? 'is-visible' : ''}`}
                style={{ transitionDelay: isVisible ? `${150 + i * 80}ms` : '0ms' }}
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#C9A84C]/20 mb-3">
                  <i className={`${item.icon} text-[#C9A84C]`} />
                </div>
                <p className="text-white font-bold text-sm mb-1">{item.title}</p>
                <p className="text-white/50 text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
